"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.BannerDefault = void 0;
require("./BannerDefault.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _renderObject = require("../../util/renderObject/renderObject");
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Banner Default Component - Default variant
 * @method
 * @param {BannerDefaultProps} props
 * @returns {JSX.Element} BannerDefault
 */
const BannerDefault = props => {
  const {
    headline,
    headlineTag = defaultProps.headlineTag,
    headlineColor = defaultProps.headlineColor,
    body,
    cornerRadius = defaultProps.cornerRadius,
    textColor = defaultProps.textColor,
    textPosition = defaultProps.textPosition,
    imagePosition = defaultProps.imagePosition,
    externalActionElement,
    externalImageElement,
    backgroundColor,
    borderColor,
    elevation = defaultProps.elevation,
    className,
    isLoading = defaultProps.isLoading,
    loaderHeightLg,
    loaderHeightMd,
    loaderHeightSm,
    loaderHeightXl
  } = props;
  if (isLoading) {
    const loaderClassName = imagePosition === 'top' ? 'pal-c-BannerDefault__Loader--fullWidth' : 'pal-c-BannerDefault__Loader';
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerDefault"
    }, /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
      className: loaderClassName,
      style: {
        '--pal-loaderghost-card-height-sm': loaderHeightSm,
        '--pal-loaderghost-card-height-md': loaderHeightMd,
        '--pal-loaderghost-card-height-lg': loaderHeightLg,
        '--pal-loaderghost-card-height-xl': loaderHeightXl
      }
    }));
  }
  const componentClassName = (0, _classnames.default)('pal-c-BannerDefault', className, {
    [`pal-c-BannerDefault--text-${textPosition}`]: textPosition,
    [`pal-c-BannerDefault--image-${imagePosition}`]: imagePosition,
    'pal-c-BannerDefault--backgroundColor': backgroundColor,
    [`pal-c-BannerDefault__elevation--${elevation}`]: elevation,
    'pal-c-BannerDefault--borderColor': borderColor,
    'pal-c-BannerDefault__corners--rounded': cornerRadius,
    'pal-c-BannerDefault--has-image': externalImageElement
  });
  const componentStyle = {
    ...(backgroundColor && {
      '--backgroundColor': `var( --pal-colors-default-${backgroundColor} )`
    }),
    ...(borderColor && {
      '--borderColor': `var( --pal-colors-default-${borderColor} )`
    }),
    ...(cornerRadius && {
      '--cornerRadius': `var( --pal-${cornerRadius} )`
    })
  };
  const headlineTextStyle = headlineColor ? headlineColor : textColor;
  const renderImage = () => {
    if (!externalImageElement) return null;
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerDefault__image-wrapper"
    }, (0, _renderObject.renderObject)(externalImageElement));
  };
  const renderTextContent = () => {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerDefault__text-wrapper"
    }, headline && /*#__PURE__*/_react.default.createElement(_Text.default, {
      htmlTag: headlineTag,
      textStyle: "title",
      className: "pal-c-BannerDefault__headline",
      textColor: headlineTextStyle
    }, headline), body && /*#__PURE__*/_react.default.createElement(_Text.default, {
      htmlTag: "p",
      className: "pal-c-BannerDefault__body",
      textColor: textColor
    }, body));
  };
  const renderActions = () => {
    if (!externalActionElement) return null;
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerDefault__actions"
    }, externalActionElement);
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: componentStyle
  }, renderImage(), /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerDefault__content"
  }, renderTextContent(), renderActions()));
};

/**
 * Property type definitions
 * @typedef BannerProps
 * @type {object}
 * @property {string} headline - Main headline text.
 * @property {string} headlineTag - HTML tag for headline (h1, h2, h3, etc.).
 * @property {string} headlineColor - Color variant for headline (none, content-brand-03, content-brand-02-strong).
 * @property {string} body - Body text content.
 * @property {string} textColor - Text color variant (content-default, content-knockout).
 * @property {string} textPosition - Desktop only: Controls body text position relative to headline (inline = next to headline, stacked = below headline). Actions always align right.
 * @property {string} imagePosition - Mobile only: Controls layout (left = image left with text right, top = full-width image with centered text below).
 * @property {array} externalActionElement - Array of action elements (buttons, links).
 * @property {object|element} externalImageElement - Image element.
 * @property {string} backgroundColor - Background color from design tokens.
 * @property {string} borderColor - Border color from design tokens (none or any border color).
 * @property {string} elevation - Shadow elevation level (none, low, medium, high).
 * @property {string} className - Extra class for the root container.
 * @property {string} cornerRadius - Sets the border radius of the banner.
 * @property {boolean} isLoading - To show the loading state of the component.
 * @property {string} loaderHeightLg - The height of the loader on LG inflections (ex - "11rem")
 * @property {string} loaderHeightMd - The height of the loader on MD inflections (ex - "11rem")
 * @property {string} loaderHeightSm - The height of the loader on SM inflections (ex - "11rem")
 * @property {string} loaderHeightXl - The height of the loader on XL inflections (ex - "11rem")
 */
exports.BannerDefault = BannerDefault;
const propTypes = exports.propTypes = {
  /**
   * Main headline text.
   */
  headline: _propTypes.default.string,
  /**
   * HTML tag for headline (h1, h2, h3, etc.).
   */
  headlineTag: _propTypes.default.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /**
   * Color variant for headline.
   */
  headlineColor: _propTypes.default.oneOf(['content-brand-03', 'content-brand-02-strong']),
  /**
   * Body text content.
   */
  body: _propTypes.default.string,
  /**
   * Sets the border radius of the banner
   */
  cornerRadius: _propTypes.default.string,
  /**
   * Text color variant.
   */
  textColor: _propTypes.default.oneOf(['content-default', 'content-knockout']),
  /**
   * Desktop only: Controls body text position relative to headline.
   * inline = body text next to headline, stacked = body text below headline.
   * Actions always align to the right.
   */
  textPosition: _propTypes.default.oneOf(['inline', 'stacked']),
  /**
   * Mobile only: Controls layout.
   * left = image left with text right, top = full-width image with centered text below.
   */
  imagePosition: _propTypes.default.oneOf(['left', 'top']),
  /**
   * Action group component
   */
  externalActionElement: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.object]),
  /**
   * Image element
   */
  externalImageElement: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.object]),
  /**
   * Background color from design tokens.
   */
  backgroundColor: _propTypes.default.string,
  /**
   * Border color from design tokens.
   */
  borderColor: _propTypes.default.string,
  /**
   * Shadow elevation level.
   */
  elevation: _propTypes.default.oneOf(['low', 'medium', 'high']),
  /**
   * Extra class for the root container.
   */
  className: _propTypes.default.string,
  /**
   * To show the loading state of the component.
   */
  isLoading: _propTypes.default.bool,
  /**
   * The height of the loader on LG inflections (ex - "11rem")
   */
  loaderHeightLg: _propTypes.default.string,
  /**
   * The height of the loader on MD inflections (ex - "11rem")
   */
  loaderHeightMd: _propTypes.default.string,
  /**
   * The height of the loader on SM inflections (ex - "11rem")
   */
  loaderHeightSm: _propTypes.default.string,
  /**
   * The height of the loader on XL inflections (ex - "11rem")
   */
  loaderHeightXl: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} borderColor=undefined - Set default borderColor of the banner to undefined
 * @property {string} cornerRadius=undefined - Set default cornerRadius to undefined
 * @property {string} elevation=undefined - Set default elevation of the banner to undefined
 * @property {string} headlineColor=undefined - Set default headlineColor to undefined
 * @property {string} headlineTag='h2' - Set default headlineTag to h2
 * @property {string} imagePosition='left' - Set default imagePosition to left
 * @property {string} textColor='content-default' - Set default textColor of the banner to content-default
 * @property {string} textPosition='inline' - Set default textPosition of the banner to inline
 * @property {boolean} isLoading=false - Set default isLoading to false
 */
const defaultProps = exports.defaultProps = {
  borderColor: undefined,
  cornerRadius: undefined,
  elevation: undefined,
  headlineColor: undefined,
  headlineTag: 'h2',
  imagePosition: 'left',
  textColor: 'content-default',
  textPosition: 'inline',
  isLoading: false
};
BannerDefault.propTypes = propTypes;
var _default = exports.default = BannerDefault;